import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeFor } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";

import Splash from "@/pages/Splash";
import Login from "@/pages/Login";

import EmployeeShell from "@/pages/employee/EmployeeShell";
import EmpHome from "@/pages/employee/Home";
import EmpRides from "@/pages/employee/MyRides";
import EmpBus from "@/pages/employee/BusSchedule";
import EmpNotif from "@/pages/employee/Notifications";
import EmpProfile from "@/pages/employee/Profile";
import EmpBookRide from "@/pages/employee/BookRide";
import EmpTracking from "@/pages/employee/RideTracking";

import DriverShell from "@/pages/driver/DriverShell";
import DriverHome from "@/pages/driver/DriverHome";
import DriverHistory from "@/pages/driver/DriverHistory";
import DriverProfile from "@/pages/driver/DriverProfile";

import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminDispatch from "@/pages/admin/Dispatch";
import AdminEmployees from "@/pages/admin/Employees";
import AdminDrivers from "@/pages/admin/Drivers";
import AdminFleet from "@/pages/admin/Fleet";
import AdminBusRoutes from "@/pages/admin/BusRoutes";
import AdminBookings from "@/pages/admin/Bookings";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminSettings from "@/pages/admin/Settings";
import AdminIntegrations from "@/pages/admin/Integrations";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function App() {
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.remove();
  }, []);
  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton />
            <Routes>
          <Route path="/" element={<Splash />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/driver" element={<Login defaultRole="driver" />} />
          <Route path="/login/admin" element={<Login defaultRole="admin" />} />
          <Route path="/console" element={<Login defaultRole="admin" />} />

          {/* Employee app */}
          <Route
            path="/app"
            element={
              <Protected roles={["employee"]}>
                <EmployeeShell />
              </Protected>
            }
          >
            <Route index element={<EmpHome />} />
            <Route path="rides" element={<EmpRides />} />
            <Route path="bus" element={<EmpBus />} />
            <Route path="notifications" element={<EmpNotif />} />
            <Route path="profile" element={<EmpProfile />} />
          </Route>
          <Route
            path="/app/book"
            element={
              <Protected roles={["employee"]}>
                <EmpBookRide />
              </Protected>
            }
          />
          <Route
            path="/app/track/:bookingId"
            element={
              <Protected roles={["employee"]}>
                <EmpTracking />
              </Protected>
            }
          />

          {/* Driver app */}
          <Route
            path="/driver"
            element={
              <Protected roles={["driver"]}>
                <DriverShell />
              </Protected>
            }
          >
            <Route index element={<DriverHome />} />
            <Route path="history" element={<DriverHistory />} />
            <Route path="profile" element={<DriverProfile />} />
          </Route>

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <Protected roles={["admin", "dispatcher"]}>
                <AdminShell />
              </Protected>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="dispatch" element={<AdminDispatch />} />
            <Route path="employees" element={<AdminEmployees />} />
            <Route path="drivers" element={<AdminDrivers />} />
            <Route path="fleet" element={<AdminFleet />} />
            <Route path="bus-routes" element={<AdminBusRoutes />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="integrations" element={<AdminIntegrations />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
